import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Play, Square, AlertTriangle, MapPin, Clock, CheckCircle2, Camera } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const hms = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

export default function JobTimerCard({ job, onStart, onFinish, onPhotos, busy }) {
  const running = job.status === "in_progress";
  const now = useNow(running);
  const start = job.actual_start ? parseISO(job.actual_start).getTime() : null;
  const plannedMs = (Number(job.duration_hours) || 0) * 3600000;
  const elapsed = running && start ? now - start : null;
  const overdue = running && plannedMs > 0 && elapsed > plannedMs;
  const done = job.status === "completed";
  const doneMs = done && start && job.actual_end ? parseISO(job.actual_end).getTime() - start : null;

  return (
    <div data-testid={`job-card-${job.id}`}
      className={`rounded-2xl border p-5 bg-card transition-colors ${overdue ? "border-destructive/60 bg-destructive/5" : running ? "border-[hsl(140_60%_45%)]/50" : "border-border/60"}`}>
      {overdue && (
        <div className="flex items-center gap-2 rounded-xl bg-destructive text-white text-sm font-medium px-4 py-2.5 mb-4 animate-pulse" data-testid={`job-overdue-${job.id}`}>
          <AlertTriangle className="w-4 h-4 shrink-0" /> Job still running past the planned finish — tap Finish when you're done
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {format(parseISO(job.scheduled_date), "h:mma")} · {job.duration_hours || "?"} h planned
          </div>
          <div className="font-serif text-xl mt-1 truncate">{job.customer?.name || "Client"}</div>
          <div className="text-sm text-muted-foreground truncate">{job.package?.name || "Clean"}</div>
          {job.address && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`} target="_blank" rel="noreferrer"
              className="text-xs text-brand inline-flex items-center gap-1 mt-1 hover:underline" data-testid={`job-card-map-${job.id}`}>
              <MapPin className="w-3.5 h-3.5" /> {job.address}
            </a>
          )}
        </div>
        {(running || done) && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{done ? "Total time" : "Timer"}</div>
            <div className={`font-mono text-2xl tabular-nums ${overdue ? "text-destructive" : done ? "text-foreground" : "text-[hsl(140_60%_32%)]"}`} data-testid={`job-timer-${job.id}`}>
              {done ? (doneMs != null ? hms(doneMs) : "—") : hms(elapsed || 0)}
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-3 mt-5">
        {job.status === "scheduled" && (
          <Button onClick={() => onStart(job)} disabled={busy} data-testid={`job-card-start-${job.id}`} className="flex-1 h-12 text-base rounded-xl gap-2">
            <Play className="w-5 h-5" /> Start job
          </Button>
        )}
        {running && (
          <Button onClick={() => onFinish(job)} disabled={busy} data-testid={`job-card-finish-${job.id}`}
            className="flex-1 h-12 text-base rounded-xl gap-2 !bg-destructive/10 !border-destructive/40">
            <Square className="w-5 h-5 text-destructive" /> <span className="text-destructive">Finish job</span>
          </Button>
        )}
        {done && (
          <div className="flex-1 h-12 rounded-xl flex items-center justify-center gap-2 text-sm text-[hsl(140_60%_32%)] bg-[hsl(140_60%_45%)]/10" data-testid={`job-card-done-${job.id}`}>
            <CheckCircle2 className="w-5 h-5" /> Completed
          </div>
        )}
        <Button variant="outline" onClick={() => onPhotos(job)} data-testid={`job-card-photos-${job.id}`} className="h-12 rounded-xl gap-2">
          <Camera className="w-4 h-4" /> {job.photos?.length || 0}
        </Button>
      </div>
    </div>
  );
}
